/**
 * 에듀집 완전 정리 가이드 - SEO 최적화 허브 페이지
 * 
 * 교사들이 "에듀집", "학습지원 소프트웨어 기준" 검색 시 유입되는 페이지
 * 왓퀴즈 서비스로 자연스럽게 전환 유도
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

const EdzipGuidePage = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>에듀집 완전정리 | 교사 5분컷 사용법·EPKI 로그인·무료체험 신청 (2026)</title>
        <meta name="description" content="에듀집(edzip) 가입·EPKI 로그인 오류 해결·무료체험 신청 방법을 현직 교사 기준 5분 안에 정리. 학습지원 소프트웨어 선정 기준 체크리스트 PDF 무료 제공. 회의자료 자동 생성 템플릿 포함." />
        <meta name="keywords" content="에듀집, edzip, 에듀집 사용법, 에듀집 로그인, 에듀집 로그인 안됨, 에듀집 EPKI, 에듀집 무료체험, 에듀집 무료체험 신청, 학습지원 소프트웨어, 학습지원 소프트웨어 기준, 학습지원 소프트웨어 선정기준, 에듀집 교사 후기, 에듀테크 플랫폼, 에듀집 회원가입" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/edzip" />
        
        {/* 네이버 웹마스터 최적화 */}
        <meta name="robots" content="index, follow" />
        <meta name="naver-site-verification" content="검증코드" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/edzip" />
        <meta property="og:title" content="에듀집 완전정리 | 교사 5분컷 가이드 · 로그인 오류 해결 · 무료체험 (2026)" />
        <meta property="og:description" content="에듀집 가입부터 EPKI 로그인, 무료체험 신청까지 5분 정리. 학습지원 소프트웨어 기준 체크리스트 무료 제공." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-edzip-guide.png" />
        <meta property="og:locale" content="ko_KR" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="에듀집 완전정리 | 교사 5분컷 가이드 (2026)" />
        <meta name="twitter:description" content="에듀집 가입·로그인·무료체험 5분 정리. 학습지원 소프트웨어 기준 체크리스트 포함." />
        
        {/* 구조화된 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "에듀집 완전 정리 | 교사 5분 사용법 가이드",
            "description": "에듀집 가입, 로그인, 무료체험 신청 방법을 교사용으로 5분 안에 정리",
            "author": {
              "@type": "Organization",
              "name": "왓퀴즈"
            },
            "publisher": {
              "@type": "Organization",
              "name": "왓퀴즈",
              "logo": "https://whatquiz.co.kr/images/logo512.png"
            },
            "datePublished": "2026-02-05",
            "dateModified": "2026-02-05"
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 섹션 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              2026년 최신 업데이트
            </span>
            
            <h1 style={{
              fontSize: 'clamp(28px, 5vw, 42px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              에듀집, 교사용 5분 요약
            </h1>
            
            <p style={{
              fontSize: '18px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              가입부터 무료체험, 후기 작성까지<br />
              <strong style={{ color: '#3182f6' }}>한 번에 정리</strong>해드립니다
            </p>
          </div>
        </section>

        {/* 에듀집이란? */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '24px'
          }}>
            에듀집이란?
          </h2>
          
          <div style={{
            padding: '24px',
            backgroundColor: '#f8fafc',
            borderRadius: '16px',
            marginBottom: '24px'
          }}>
            <p style={{
              fontSize: '16px',
              color: '#374151',
              lineHeight: '1.8',
              margin: 0
            }}>
              <strong>에듀집(edzip.kr)</strong>은 교육부·KERIS가 주관하고 건국대가 운영하는 
              <strong style={{ color: '#3182f6' }}> 공교육 공식 에듀테크 정보·체험 플랫폼</strong>입니다.
              <br /><br />
              2025년 8월 개통 후, 교사가 에듀테크를 탐색하고 체험하는 "공식 출발점"으로 자리 잡았습니다.
            </p>
          </div>

          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            교사가 에듀집으로 할 수 있는 것
          </h3>
          
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {[
              '에듀테크 무료체험 신청 및 사용',
              '다른 선생님들의 후기·수업자료 확인',
              '제품 비교 및 학교장터 연계 구매',
              '학습지원 소프트웨어 기준 충족 여부 확인'
            ].map((item, idx) => (
              <li key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#dcfce7',
                  color: '#16a34a',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>✓</span>
                <span style={{ fontSize: '15px', color: '#374151' }}>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 학습지원 소프트웨어 기준 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '24px'
            }}>
              학습지원 소프트웨어 선정 기준
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              2025년 12월 교육부가 발표한 <strong>"학교의 안전하고 효과적인 학습지원 소프트웨어 선정을 위한 기준"</strong>에 따라,
              이제 학교장이 에듀테크를 도입할 때 이 기준을 반드시 확인해야 합니다.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '24px',
                backgroundColor: '#fff',
                borderRadius: '16px',
                border: '2px solid #3182f6'
              }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#3182f6',
                  marginBottom: '12px'
                }}>필수 기준 (5개)</h4>
                <ul style={{
                  listStyle: 'disc',
                  paddingLeft: '20px',
                  margin: 0,
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.8'
                }}>
                  <li>개인정보보호 법령 준수</li>
                  <li>학습데이터 수집·활용 투명성</li>
                  <li>보안 취약점 점검 및 조치</li>
                  <li>접근성 보장</li>
                  <li>윤리적 AI 활용</li>
                </ul>
              </div>
              
              <div style={{
                padding: '24px',
                backgroundColor: '#fff',
                borderRadius: '16px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '12px'
                }}>선택 기준 (권장)</h4>
                <ul style={{
                  listStyle: 'disc',
                  paddingLeft: '20px',
                  margin: 0,
                  fontSize: '14px',
                  color: '#64748b',
                  lineHeight: '1.8'
                }}>
                  <li>교육과정 연계성</li>
                  <li>학습 효과성 검증</li>
                  <li>교사·학생 편의성</li>
                  <li>기술 지원 체계</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 왓퀴즈 CTA 섹션 */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              에듀집 때문에 늘어난 업무,<br />
              <span style={{ color: '#3182f6' }}>자동으로 줄여드릴게요</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              학습지원 소프트웨어 선정 회의자료, 생기부, 가정통신문<br />
              왓퀴즈 AI가 3분 만에 작성해드립니다.
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center'
            }}>
              <button
                onClick={() => navigate('/create-saenggibu')}
                style={{
                  padding: '16px 40px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: '#3182f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                무료로 생기부 써보기
              </button>
              
              <button
                onClick={() => navigate('/chat')}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#3182f6',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                AI 교사 상담 체험하기 →
              </button>
            </div>

            <p style={{
              marginTop: '24px',
              fontSize: '13px',
              color: '#94a3b8'
            }}>
              현직 교사 {TEACHER_COUNT_LABEL} 이상이 사용 중 · 개인정보 수집 Zero
            </p>
          </div>
        </section>

        {/* FAQ 섹션 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '24px'
          }}>
            자주 묻는 질문
          </h2>

          {[
            {
              q: '에듀집 로그인이 안 될 때는?',
              a: 'EPKI(교육부 PKI) 인증서 설치 여부를 확인하세요. 크롬 브라우저에서 EPKI 플러그인이 정상 작동하는지 체크하시고, 안 되면 에듀집 고객센터에 문의하시면 됩니다.'
            },
            {
              q: '무료체험은 어떻게 신청하나요?',
              a: '에듀집 로그인 후 [에듀테크 탐색] → 원하는 서비스 선택 → [무료체험 신청] 버튼을 클릭하시면 됩니다. 승인까지 1~3일 정도 소요됩니다.'
            },
            {
              q: '학습지원 소프트웨어 기준은 누가 확인해야 하나요?',
              a: '학교장 또는 정보담당 부장교사가 확인합니다. 에듀집에서 등록된 서비스는 기본적인 기준을 충족했다고 볼 수 있지만, 학교 자체 심의는 별도로 필요합니다.'
            }
          ].map((faq, idx) => (
            <div key={idx} style={{
              padding: '20px 0',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111',
                marginBottom: '8px'
              }}>
                Q. {faq.q}
              </h4>
              <p style={{
                fontSize: '15px',
                color: '#64748b',
                lineHeight: '1.7',
                margin: 0
              }}>
                A. {faq.a}
              </p>
            </div>
          ))}
        </section>

        <Footer />
      </div>
    </>
  );
};

export default EdzipGuidePage;
